// @ts-nocheck
import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { Phone, AlertTriangle, Package, MessageCircle } from 'lucide-react'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

/**
 * 通話履歴のフォーマット関数を提供するフック
 * CallHistoryTable, CallMemoEditor, CustomerDetailModal, CustomerSidePanel で共通利用
 *
 * 注意: 関数はすべてメモ化されており、再レンダリングを防ぐ
 */
export function useCallFormatters() {
  return useMemo(() => {
    /**
     * 通話時間のフォーマット (秒 → "M:SS")
     */
    const formatDuration = (seconds) => {
      if (!seconds) return '0:00'
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    }

    /**
     * 日時のフォーマット
     * @param {string} isoString - ISO形式の日時文字列
     * @param {object} options - フォーマットオプション
     * @param {string} options.format - 'default'(年月日), 'full', 'compact', 'smart'
     */
    const formatDateTime = (isoString, options = {}) => {
      if (!isoString) return ''
      const { format = 'default' } = options

      try {
        const dt = DateTime.fromISO(isoString)
        const now = DateTime.now()

        switch (format) {
          case 'full':
            return dt.toFormat('yyyy年M月d日 HH時mm分ss秒')
          case 'compact':
            return dt.toFormat('M/d HH時mm分ss秒')
          case 'smart':
            if (dt.hasSame(now, 'day')) {
              return dt.toFormat('HH時mm分ss秒')
            } else if (dt.hasSame(now, 'year')) {
              return dt.toFormat('M月d日 HH時mm分ss秒')
            } else {
              return dt.toFormat('yyyy年M月d日 HH時mm分ss秒')
            }
          case 'default':
          default:
            // 一覧表示用: 常に年月日と秒までを表示
            return dt.toFormat('yyyy年M月d日 HH時mm分ss秒')
        }
      } catch {
        return isoString
      }
    }

    /**
     * 電話番号を国内表示形式にフォーマット
     * phoneNumberHelperのformatPhoneNumberForDisplayを使用
     */
    const formatPhoneNumber = (phoneNumber) => {
      return formatPhoneNumberForDisplay(phoneNumber)
    }

    /**
     * 通話種別のラベルを取得
     */
    const getCallTypeLabel = (callType) => {
      switch (callType) {
        case 'inquiry':
          return '問合せ'
        case 'complaint':
          return 'クレーム'
        case 'order':
          return '注文'
        case 'other':
          return 'その他'
        default:
          return '未分類'
      }
    }

    /**
     * 通話種別のアイコンを取得
     */
    const getCallTypeIcon = (callType) => {
      const iconClass = "w-4 h-4"
      switch (callType) {
        case 'inquiry':
          return <Phone className={`${iconClass} text-blue-600`} />
        case 'complaint':
          return <AlertTriangle className={`${iconClass} text-red-600`} />
        case 'order':
          return <Package className={`${iconClass} text-green-600`} />
        default:
          return <MessageCircle className={`${iconClass} text-gray-600`} />
      }
    }

    /**
     * 通話種別の色を取得
     */
    const getCallTypeColor = (callType) => {
      switch (callType) {
        case 'inquiry':
          return 'text-blue-600'
        case 'complaint':
          return 'text-red-600'
        case 'order':
          return 'text-green-600'
        default:
          return 'text-gray-600'
      }
    }

    /**
     * ステータスバッジを取得
     */
    const getStatusBadge = (status) => {
      switch (status) {
        case 'completed':
          return {
            label: '完了',
            className: 'px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full'
          }
        case 'missed':
          return {
            label: '不在着信',
            className: 'px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full'
          }
        case 'no-answer':
          return {
            label: '未応答',
            className: 'px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full'
          }
        case 'voicemail':
          return {
            label: '留守電',
            className: 'px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full'
          }
        default:
          return {
            label: status || '不明',
            className: 'px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full'
          }
      }
    }

    /**
     * ステータスバッジをレンダリング
     */
    const renderStatusBadge = (status) => {
      const badge = getStatusBadge(status)
      return <span className={badge.className}>{badge.label}</span>
    }

    return {
      formatDuration,
      formatDateTime,
      formatPhoneNumber,
      getCallTypeLabel,
      getCallTypeIcon,
      getCallTypeColor,
      getStatusBadge,
      renderStatusBadge
    }
  }, []) // 依存配列が空なので、コンポーネントのマウント時に一度だけ作成
}
